"use client";

import CoWorkersWorkshop from "@/components/Views/Workshop/CoWorkersWorkshop";
import FlowsWorkshop from "@/components/Views/Workshop/FlowsWorkshop";
import KnowledgeBasesWorkshop from "@/components/Views/Workshop/KnowledgeBasesWorkshop";
import TablesWorkshop from "@/components/TableTool/TablesWorkshop";
import { Ban, Inbox, Upload } from "lucide-react";
import { ChangeEventHandler, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useSpotUser } from "@/utils/spotUser";
import { usePermissions } from "@/hooks/usePermissions";
import {
  CASES_PERMS,
  RESOURCE_VIEW_PERMS,
  WORKSPACE_PERMISSIONS,
} from "@/constants/permissions";
import { isCaseManagementEnabled } from "@/utils/modules";
import { useInboxes } from "@/components/Spotflow/Inbox/useInboxes";
import { useGeniePage } from "@/lib/genie";
import { CreateToolButton } from "@/components/CreateToolButton";
import { useSetAtom } from "jotai";
import { importCsvFileAtom } from "@/store/ui-atoms";
import { FileDropZone } from "@/components/Form/FileDropZone";
import {
  areValidFileTypes,
  FileTypes,
  getMaxFileSize,
  isSpreadsheet,
} from "@/utils/mimetypes";
import { withinImportLimit } from "@/utils/csv";
import { SpotToast } from "@/components/SpotToast";
import { WorkflowService } from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { NoxusEmptyBlock } from "@/components/UI/NoxusEmptyBlock";

import { StandardLayout } from "@/components/Layouts/StandardLayout";
import { useSections } from "@/components/Layouts/pageSections";
import {
  ImportOptionsDialog,
  ImportOptions,
} from "@/components/Dialogs/ImportOptionsDialog";

const IMPORTABLE_FILE_TYPES = [FileTypes.JSON, FileTypes.CSV, FileTypes.XLSX];

const DropOverlay = () => {
  return (
    <NoxusEmptyBlock
      iconComponent={<Upload size={40} className="text-foreground" />}
      title="Drop file to import"
      description="Drop a .nx to import a tool, or a .csv / .xlsx to import a table."
    />
  );
};

// Knowledge Bases and Tables sit under Data in the sidenav.
const DATA_BREADCRUMBS = [{ label: "Data" }];

const WorkshopPage = () => {
  const buildSections = useSections("workshop");
  const router = useRouter();
  const searchParams = useSearchParams();
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const [isImporting, setIsImporting] = useState(false);
  const [pendingImport, setPendingImport] = useState<{
    file: File;
    content: string;
  } | null>(null);
  const { hasAnyPermission, hasPermission } = usePermissions();
  const canView = hasAnyPermission(RESOURCE_VIEW_PERMS, "group");
  const canEdit = hasPermission(WORKSPACE_PERMISSIONS.EDIT, "group");
  const isCasesOnly =
    !canView &&
    isCaseManagementEnabled(spotGroup) &&
    hasAnyPermission(CASES_PERMS, "group");
  const { inboxes, isLoading: isLoadingInboxes } = useInboxes(isCasesOnly);

  // Tables-tab machinery (folded in from the old /data page).

  const setImportCsvFile = useSetAtom(importCsvFileAtom);

  // The main sidenav picks the section (Flows, Agents, Knowledge Bases, Tables).
  const workshopSections = buildSections(
    canView
      ? {
          flows: <FlowsWorkshop />,
          agents: <CoWorkersWorkshop />,
          "knowledge-bases": <KnowledgeBasesWorkshop />,
          tables: <TablesWorkshop />,
        }
      : {}
  );
  const activeSection =
    workshopSections.find((tab) => tab.id === searchParams?.get("tab")) ??
    workshopSections[0];
  const currentSection = activeSection?.id ?? "flows";

  const isDataSection =
    currentSection === "knowledge-bases" || currentSection === "tables";

  useGeniePage(
    "Build lists (home): browse and create flows, agents, knowledge bases and " +
      `tables. Active section: ${currentSection}`
  );

  const importWorkflowMutation = useMutation({
    mutationFn: async ({
      file,
      options,
    }: {
      file: File;
      options: ImportOptions;
    }) => {
      const fileContent = await file.text();

      // Forward import options as query params. Cast `as any` until openapi
      // regen picks up the new params on the generated client.
      const query: Record<string, string | boolean> = {
        mode: options.mode,
      };
      if (options.activate) query.activate = true;
      if (options.on_missing_dependency !== "fail")
        query.on_missing_dependency = options.on_missing_dependency;

      return WorkflowService.importWorkflow({
        path: { group_id: spotGroup.id },
        body: {
          definition: fileContent,
        },
        query: query as any,
      });
    },
    onMutate: () => {
      const toastId = SpotToast.loading("Importing the provided file...");
      return { toastId };
    },
    onSuccess: (response: any, _variables, context) => {
      SpotToast.success(
        "Imported successfully",
        undefined,
        undefined,
        context?.toastId
      );
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKFLOWS] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.WORKSHOP_RECENT] });
      queryClient.invalidateQueries({ queryKey: [DATA_KEYS.ASSISTANTS] });

      // Secret values never travel in a bundle: the refs are rebound to this
      // workspace's variables by name, and anything unmatched lands as an empty
      // placeholder. Either way the run fails on a missing value with no hint,
      // so name them here instead of letting the success toast imply it's done.
      const secretNames = response.data
        .filter((item: any) => item.type === "secret")
        .map((item: any) => item.name)
        .filter(Boolean);
      if (secretNames.length > 0) {
        SpotToast.warn(
          `Check ${secretNames.length === 1 ? "this workspace secret has a value" : "these workspace secrets have values"}: ${secretNames.join(", ")}`
        );
      }
      // Connections travel as identity only and may land as placeholders
      // that need re-authorizing before anything bound to them can run.
      const connectionNames = response.data
        .filter((item: any) => item.type === "connection")
        .map((item: any) => item.name)
        .filter(Boolean);
      if (connectionNames.length > 0) {
        SpotToast.warn(
          `Reconnect ${connectionNames.length === 1 ? "this connection" : "these connections"} in Settings: ${connectionNames.join(", ")}`
        );
      }
      const items = response.data.filter((item: any) => {
        return (
          item.type === "workflow" ||
          item.type === "assistant" ||
          item.type === "kb"
        );
      });
      if (items.length === 0) {
        return;
      }
      const item = items[0];

      if (item.type === "assistant") {
        router.push(`/assistant/${item.reference}`);
      } else if (item.type === "kb") {
        router.push(`/data-hub/${item.reference}`);
      } else {
        router.push(`/flows/${item.reference}`);
      }
    },
    onError: (error: any, _variables, context) => {
      const axiosError = error as { response?: { data?: { detail?: string } } };
      const errorMessage =
        axiosError?.response?.data?.detail ||
        "There was an error importing your file. Please check the file format and try again.";
      SpotToast.error(errorMessage, undefined, undefined, context?.toastId);
    },
    onSettled: () => {
      setIsImporting(false);
    },
  });

  const handleFileUpload = async (files: FileList) => {
    const file = files[0];
    if (!file) return;

    if (isSpreadsheet(file.name)) {
      if (withinImportLimit(file)) setImportCsvFile(file);
      return;
    }

    if (!file.name.endsWith(".nx")) {
      SpotToast.error("Please select a .nx, .csv or .xlsx file");
      return;
    }

    const content = await file.text();
    setPendingImport({ file, content });
  };

  const handleImportConfirm = (options: ImportOptions) => {
    if (!pendingImport) return;
    setIsImporting(true);
    importWorkflowMutation.mutate({ file: pendingImport.file, options });
    setPendingImport(null);
  };

  const processFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (!areValidFileTypes(files, IMPORTABLE_FILE_TYPES)) {
      SpotToast.error("Invalid file format");
      return;
    }

    if (files.length !== 1) {
      SpotToast.error("You can only upload one file.");
      return;
    }

    const MAX_FILE_SIZE = getMaxFileSize();

    const hasFilesTooBig = Array.from(files).some(
      (file) => file.size > MAX_FILE_SIZE
    );
    if (hasFilesTooBig) {
      SpotToast.error(
        `File size exceeds the ${MAX_FILE_SIZE / 1024 / 1024}MB limit.`
      );
      return;
    }

    handleFileUpload(files);
    return files;
  };

  const headerActions = canView && (
    <CreateToolButton tab={currentSection} isDisabled={isImporting} />
  );

  return (
    <>
      <title>{`${activeSection?.title ?? "Flows"} - Noxus`}</title>
      <FileDropZone
        style="blur"
        acceptedFileTypes={IMPORTABLE_FILE_TYPES}
        multiple={false}
        onFiles={(files) => {
          processFiles(files);
        }}
        dragOverlay={<DropOverlay />}
        className="flex flex-col flex-1 min-h-0 h-full"
      >
        <ImportOptionsDialog
          isOpen={pendingImport !== null}
          onOpenChange={(open) => !open && setPendingImport(null)}
          fileContent={pendingImport?.content ?? ""}
          fileName={pendingImport?.file.name ?? ""}
          isImporting={importWorkflowMutation.isPending}
          onConfirm={handleImportConfirm}
        />
        <StandardLayout
          sections={workshopSections}
          breadcrumbs={isDataSection ? DATA_BREADCRUMBS : undefined}
          headerActions={headerActions}
          isLoading={isCasesOnly && isLoadingInboxes}
          noScroll={!canView}
        >
          {!canView &&
            (isCasesOnly ? (
              <CasesOnlyEmptyState hasInboxes={inboxes.length > 0} />
            ) : (
              <NoxusEmptyBlock
                icon={Ban}
                title="Nothing to open here yet"
                description="Your role in this workspace does not include any pages. Ask an admin of this workspace for access."
              />
            ))}
        </StandardLayout>
      </FileDropZone>
    </>
  );
};

// A role that can only work cases has no tools to list here.
const CasesOnlyEmptyState = ({ hasInboxes }: { hasInboxes: boolean }) =>
  hasInboxes ? (
    <NoxusEmptyBlock
      icon={Inbox}
      title="Your work is in your inboxes"
      description="Open an inbox from the sidebar to see its cases and tasks."
    />
  ) : (
    <NoxusEmptyBlock
      icon={Inbox}
      title="No inboxes yet"
      description="Inboxes added to this workspace will appear in the sidebar."
    />
  );

export default WorkshopPage;
